import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { defer, LoaderFunctionArgs, useNavigate, useParams } from 'react-router-dom';
import { TrashIcon } from '@heroicons/react/24/outline';
import { QueryClient } from '@tanstack/react-query';
import { TFunction } from 'i18next';

import { createHandle } from 'services/breadcrumbs/breadcrumbs';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useDeactivateRelationship, useRelationship } from 'services/repositories/relationships/relationships';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { getTradePartner } from 'support/helpers/relationships/relationships';
import { RouteParams } from 'support/helpers/types/types';
import { ConnectionsRoutes, NetworkRoutes, RelationshipV2 } from 'support/types';
import {
  ConnectionNavigationProvider,
  ConnectionOrigin,
  useConnectionNavigation,
} from 'pages/Connections/ConnectionDetailsPage/contexts/ConnectionNavigationContext';
import { ConfirmDeletePopup } from 'components/Display/ConfirmDeletePopup/ConfirmDeletePopup';
import { Button } from 'components/Form/Button/Button';
import { Page } from 'components/Page/Page';
import { Translate } from 'components/Translate/Translate';

import { useSetupConnectionSteps } from './hooks/useSetupConnectionSteps/useSetupConnectionSteps';
import { RelationshipDetailsBanner } from './components/RelationshipDetailsBanner/RelationshipDetailsBanner';
import { RelationshipDetailsSidebar } from './components/RelationshipDetailsSidebar/RelationshipDetailsSidebar';
import { RelationshipDetailsSlideOvers } from './components/RelationshipDetailsSlideOvers/RelationshipDetailsSlideOvers';
import { RelationshipDetailsTabs } from './components/RelationshipDetailsTabs/RelationshipDetailsTabs';

const BEFORE_LIVE_RELATIONSHIP_STATUS: Array<RelationshipV2['status']> = [
  'PARTNER_APPROVAL_PENDING',
  'PARTNER_INTEGRATION_PENDING',
  'PARTNER_ONBOARDING_PENDING',
  'PARTNER_PROCESS_PENDING',
  'ISSUE_RESOLVER_PENDING',
  'READY_FOR_TESTING',
  'HAS_TEST_MESSAGES',
  'IN_REVIEW',
];

type RelationshipDetailsPageParams = RouteParams<typeof ConnectionsRoutes.relationshipDetails>;
type NetworkRelationshipDetailsPageParams = RouteParams<typeof NetworkRoutes.relationshipDetails>;

type RelationshipDetailsPageProps = {
  origin: ConnectionOrigin;
};

export const RelationshipDetailsPage = ({ origin }: RelationshipDetailsPageProps) => {
  return (
    <ConnectionNavigationProvider origin={origin}>
      <RelationshipDetailsPageContent />
    </ConnectionNavigationProvider>
  );
};

const RelationshipDetailsPageContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partnerId, relationshipId } = useParams<RelationshipDetailsPageParams>();
  const { routeTo } = useConnectionNavigation();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const { data: currentPartner } = useCurrentPartner();
  const { data: relationship, isLoading: isLoadingRelationship } = useRelationship({
    variables: { relationshipId },
    keepPreviousData: true,
    staleTime: 0,
    enabled: Boolean(relationshipId),
  });
  const { steps, pendingTasksCount } = useSetupConnectionSteps({
    relationship,
    partnerId,
    currentPartnerId: currentPartner?.id,
  });
  const { mutate, isLoading: isDeactivatingRelationship } = useDeactivateRelationship();

  const isLoading = !relationship || !currentPartner || isLoadingRelationship;
  const otherPartner = relationship && currentPartner && getTradePartner(relationship, currentPartner.id);
  const isRelationshipDeactivated = relationship?.status === 'DEACTIVATED';

  const cancelActionTexts = {
    buttonLabel: t('connections:cancelRequest.label'),
    confirmTitle: t('connections:cancelRequest.confirm.title'),
    confirmDescription: <Translate i18nKey="connections:cancelRequest.confirm.description" />,
    confirmCancelButton: t('connections:cancelRequest.confirm.cancelButton'),
    confirmDeleteButton: t('connections:cancelRequest.confirm.deleteButton'),
  };
  const deactivateActionTexts = {
    buttonLabel: t('connections:deactivateRequest.label'),
    confirmTitle: t('connections:deactivateRequest.confirm.title'),
    confirmDescription: <Translate i18nKey="connections:deactivateRequest.confirm.description" />,
    confirmCancelButton: t('connections:deactivateRequest.confirm.cancelButton'),
    confirmDeleteButton: t('connections:deactivateRequest.confirm.deleteButton'),
  };
  const userCanCancelRequest = relationship && BEFORE_LIVE_RELATIONSHIP_STATUS.includes(relationship.status);
  const actionTexts = userCanCancelRequest ? cancelActionTexts : deactivateActionTexts;

  if (!partnerId || !relationshipId) {
    return null;
  }

  const handleDeactivate = () => {
    if (!otherPartner || !relationship) {
      return;
    }

    mutate(
      {
        relationshipId: relationship.id,
        partnerId: otherPartner.id,
      },
      {
        onSuccess: () => {
          setShowConfirmDelete(false);
          navigate(routeTo('connectionDetails', { partnerId: otherPartner.id }));
        },
      },
    );
  };

  return (
    <Page isLoading={isLoading}>
      <Page.Head
        rightContent={
          !isLoading &&
          !isRelationshipDeactivated && (
            <Button
              onClick={() => setShowConfirmDelete(true)}
              LeftIcon={TrashIcon}
              variant="secondary-danger"
              analyticsId="connections:deactivate_request"
            >
              {actionTexts.buttonLabel}
            </Button>
          )
        }
      />
      <Page.Section
        isLoading={isLoading}
        topBanner={
          relationship &&
          currentPartner && (
            <RelationshipDetailsBanner relationship={relationship} pendingTasksCount={pendingTasksCount} />
          )
        }
        rightAside={
          relationship && currentPartner ? (
            <RelationshipDetailsSidebar relationship={relationship} currentPartner={currentPartner} />
          ) : undefined
        }
      >
        {relationship && <RelationshipDetailsTabs relationship={relationship} steps={steps} />}
      </Page.Section>

      {otherPartner && relationship && (
        <ConfirmDeletePopup
          open={showConfirmDelete}
          isDeleting={isDeactivatingRelationship}
          onClose={() => setShowConfirmDelete(false)}
          onDelete={handleDeactivate}
          title={actionTexts.confirmTitle}
          description={actionTexts.confirmDescription}
          cancelText={actionTexts.confirmCancelButton}
          deleteText={actionTexts.confirmDeleteButton}
        />
      )}

      <RelationshipDetailsSlideOvers />
    </Page>
  );
};

type RelationshipDetailsPageData = RelationshipV2;

RelationshipDetailsPage.loader = (queryClient: QueryClient) => async (args: LoaderFunctionArgs) => {
  const relationshipId = args.params.relationshipId;
  if (!relationshipId) {
    return defer({ data: Promise.resolve(undefined) });
  }

  const queryKey = useRelationship.getKey({ relationshipId });
  const cachedData = queryClient.getQueryData<RelationshipDetailsPageData>(queryKey);

  return defer({
    data: cachedData ? Promise.resolve(cachedData) : queryClient.fetchQuery(queryKey, useRelationship.queryFn),
  });
};

RelationshipDetailsPage.handle = createHandle<RelationshipDetailsPageData, RelationshipDetailsPageParams>(
  (params, data) => {
    return {
      label: (t: TFunction) => (data ? t(`common:messageTypes.plural.${data.message_type}`) : '...'),
      to: routeToPage(ConnectionsRoutes.relationshipDetails, {
        partnerId: params.partnerId,
        relationshipId: params.relationshipId,
      }),
      key: `relationship-details-${params.relationshipId}`,
    };
  },
);

RelationshipDetailsPage.handleFromNetwork = createHandle<
  RelationshipDetailsPageData,
  NetworkRelationshipDetailsPageParams
>((params, data) => {
  return {
    label: (t: TFunction) => (data ? t(`common:messageTypes.plural.${data.message_type}`) : '...'),
    to: routeToPage(NetworkRoutes.relationshipDetails, {
      partnerId: params.partnerId,
      relationshipId: params.relationshipId,
    }),
    key: `network-relationship-details-${params.relationshipId}`,
  };
});
